import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';

export interface StepperStepProps {
  children: React.ReactNode;
  description?: string;
  title: string;
  id?: string;
}

/**
 * @deprecated Try StepperV2 instead
 */
export function StepperStep(_props: StepperStepProps): JSX.Element | null {
  // Note: This functional component provides a public interface, but is not meant to be directly rendered.
  // `Stepper` will actually render the private `StepperStepSection`.
  return null;
}

/**
 * A private implementation of each step section. Since certain props must be generated internally by
 * `Stepper`, do not render this component outside this file.
 */
function StepperStepSection(
  props: StepperStepProps & {stepNumber: number; isFinalStep: boolean}
): JSX.Element {
  return (
    <>
      <div className={css(stepperStyles.sectionNumber)}>{props.stepNumber}</div>
      <div className={css(stepperStyles.sectionTitle)}>{props.title}</div>
      <div className={css(stepperStyles.sectionContent, props.isFinalStep && stepperStyles.finalStep)}>
        {props.description == null ? null : (
          <div className={css(stepperStyles.sectionDescription)}>{props.description}</div>
        )}
        {props.children}
      </div>
    </>
  );
}

export interface StepperProps {
  children:
    | Array<React.ReactElement<StepperStepProps> | boolean | null>
    | React.ReactElement<StepperStepProps>;
}

/**
 * `Stepper` displays a vertical stack of steps, automatically handling numbering each step.
 *
 * Usage:
 *    <Stepper>
 *      <StepperStep title="The first step" key="first">
 *        <...>
 *      </StepperStep>
 *      <StepperStep title="The next step" key="second">
 *         <...>
 *      </StepperStep>
 *      {filterConfig == null ? null : <StepperStep title="Filter step" key="filter">...</StepperStep>}
 *    </Stepper>
 *
 * All children of `Stepper` should be `StepperStep` (or null or false to hide a step).
 *
 * Including an `id` on each `StepperStep` is recommended if steps are dynamically hidden or reordered.
 *
 * While `Stepper` currently supports only numbered steps, the component could be extended to show status
 * icons/symbols instead.
 */
/**
 * @deprecated Try StepperV2 instead
 */
export default function Stepper(props: StepperProps): JSX.Element {
  return (
    <div>
      {React.Children.toArray(props.children).map(
        (step: React.ReactElement<StepperStepProps>, index, steps) => (
          <StepperStepSection
            {...step.props}
            key={step.props.id ?? index}
            isFinalStep={index === steps.length - 1}
            stepNumber={index + 1}
          />
        )
      )}
    </div>
  );
}

// TODO: phase out Stepper
interface StepLayoutProps {
  number: React.ReactNode;
  hideVerticalRule: boolean;
  title: React.ReactNode;
  content: React.ReactNode;
  description: React.ReactNode;
}

function StepLayout({number, hideVerticalRule, title, description, content}: StepLayoutProps): JSX.Element {
  return (
    <div className={css(stepperStyles.stepContainer)}>
      <div>{number}</div>
      <div>{title}</div>
      {/* vertical rule using css */}
      <div
        className={css(stepperStyles.stepBorder, hideVerticalRule && stepperStyles.stepBorder___FinalStep)}
      />
      <div>
        <div>{description}</div>
        <div>{content}</div>
      </div>
    </div>
  );
}

function defaultNumberRenderer(i: number): React.ReactNode {
  return <span className={css(stepperStyles.defaultStepNumber)}>{i}</span>;
}

export interface StepObject {
  title: React.ReactNode;
  content: React.ReactNode;
  renderNumber?: number | string | JSX.Element | ((i: number) => React.ReactNode);
  description?: React.ReactNode;
  key: string;
}
export interface StepperV2Props {
  steps: Array<StepObject | false | null>;
  hideVerticalRule?: boolean;
}

/**
 * Displays a vertical stack of steps, automatically handling numbering each step.
 * Users have full control of the steps' title and content by composition.
 *
 * How to extend:
 * One can extend this component with a wrapper:
 * <MyTeamsStepper
 *   steps={[{title, content}]}
 * />
 *
 * And inside MyTeamsStepper:
 * ```
 * wrappedSteps = steps.map(stepObj=>({...stepObj, title: <div className='margin-3'>{stepObj.title}</div>}))
 * ```
 *
 * Future development upon request + convincing use cases:
 *   1. customLayout: {number, isLast, title, description, content}: StepLayoutProps) => React.ReactNode
 *
 * @example
 * <StepperV2
 *   steps={[
 *     {
 *       title: <h1>Title A</h1>,
 *       content: <p>Content A</p>,
 *     },
 *     renderThisStep && {
 *       title: <h1>Title B</h1>,
 *       content: <p>Content B</p>,
 *     },
 *   ]}
 * />
 *
 * @param steps - An array of step objects. Each step object must have a title, content, and key.
 *                renderNumber is a field of step object that allows you to overide step number rendering.
 *                It can be null/undefined and the default renderer will be used. It can also be a ReactNode
 *                or a function that receieves 1-based step number to render your own component dynamically.
 */
export function StepperV2({steps, hideVerticalRule = false}: StepperV2Props): JSX.Element {
  const renderableSteps = steps.filter((step) => step != null && step !== false);

  // check keys are unique
  const keySet = new Set<string>();
  renderableSteps.forEach(({key}) => {
    if (keySet.has(key)) {
      throw new Error(`Duplicate key found in StepperV2: ${key}`);
    }
    keySet.add(key);
  });

  return (
    <div className={css(stepperStyles.stepperContainer)}>
      {renderableSteps.map(({title, description, content, key, renderNumber}, i) => (
        <StepLayout
          key={key}
          number={
            (renderNumber == null && defaultNumberRenderer(i + 1)) ||
            (typeof renderNumber === 'function' && renderNumber(i + 1)) ||
            renderNumber
          }
          hideVerticalRule={hideVerticalRule || i === renderableSteps.length - 1}
          title={title}
          content={content}
          description={description}
        />
      ))}
    </div>
  );
}

const CIRCLE_RADIUS = 10;
const MARGIN_AROUND_CIRCLE = 4;
// The gap between the circle and the content equals the height of the circle
const CONTENT_PADDING_TOP = CIRCLE_RADIUS * 2 - MARGIN_AROUND_CIRCLE;
// There is more padding between steps
const CONTENT_PADDING_BOTTOM = CONTENT_PADDING_TOP * 2;

export const stepperStyles = StyleSheet.create({
  finalStep: {
    borderLeft: '1px solid transparent', // Prevent content from shifting 1px left on final step
  },
  sectionContent: {
    borderLeft: `1px solid ${Colors.GRAY_LIGHT}`,
    marginBottom: MARGIN_AROUND_CIRCLE,
    marginLeft: CIRCLE_RADIUS,
    marginTop: MARGIN_AROUND_CIRCLE,
    paddingBottom: CONTENT_PADDING_BOTTOM,
    paddingLeft: CIRCLE_RADIUS * 2,
    paddingTop: CONTENT_PADDING_TOP,
  },
  sectionDescription: {
    color: Colors.GRAY_DARKER,
    fontSize: 13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    // The description should nest within the content so the left border extends beside it, but should
    // appear just beneath the title. Translate the description up within the contents' margin and padding.
    transform: `translateY(-${CONTENT_PADDING_TOP + MARGIN_AROUND_CIRCLE}px)`,
  },
  sectionTitle: {
    color: Colors.NEAR_BLACK,
    fontSize: 15,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: `${CIRCLE_RADIUS * 2}px`, // Title is centered with circle
  },
  sectionNumber: {
    border: `1px solid ${Colors.GRAY_DARKER}`,
    borderRadius: CIRCLE_RADIUS,
    color: Colors.GRAY_DARKER,
    float: 'left',
    fontSize: 13,
    fontWeight: 500,
    height: CIRCLE_RADIUS * 2,
    lineHeight: '17px',
    marginRight: CIRCLE_RADIUS,
    textAlign: 'center',
    width: CIRCLE_RADIUS * 2,
  },
  stepperContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  stepContainer: {
    display: 'grid',
    gridTemplateColumns: `auto 1fr`,
    rowGap: '5px',
    columnGap: '10px',
    alignItems: 'center',
  },
  stepBorder: {
    width: '1px',
    height: '100%',
    backgroundColor: Colors.GRAY_LIGHT,
    margin: '0 auto',
  },
  stepBorder___FinalStep: {
    backgroundColor: 'transparent',
  },
  // Nice defaults, but users need to apply these themselves using jsx injection
  // title: <div className='defaultStepNumber'>{title}</div
  defaultStepNumber: {
    border: `1px solid ${Colors.GRAY_DARKER}`,
    borderRadius: CIRCLE_RADIUS,
    color: Colors.GRAY_DARKER,
    float: 'left',
    fontSize: 13,
    fontWeight: 500,
    height: CIRCLE_RADIUS * 2,
    lineHeight: '17px',
    textAlign: 'center',
    width: CIRCLE_RADIUS * 2,
  },
  defaultStepTitle: {
    color: Colors.NEAR_BLACK,
    fontSize: 15,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: `${CIRCLE_RADIUS * 2}px`,
  },
  defaultStepDescription: {
    color: Colors.GRAY_DARKER,
    fontSize: 13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  defaultStepContent: {
    paddingTop: CIRCLE_RADIUS * 2,
    paddingBottom: CIRCLE_RADIUS * 3,
  },
});
